// src/contexts/servicos/TecnicoResponsavelContext.js
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import TecnicoResponsavelCad from '../../pages/servicos/tecnicos/TecnicoResponsavelCad';
import TecnicoResponsavelSearch from '../../pages/servicos/tecnicos/TecnicoResponsavelSearch';
import * as service from '../../services/servicos/TecnicoResponsavelService';
import { useAlert } from '../geral/AlertContext';
import { useMessage } from '../geral/MessageContext';

const TecnicoResponsavelContext = createContext();

export const useTecnicoResponsavel = () => {
  const context = useContext(TecnicoResponsavelContext);
  if (!context) {
    throw new Error('useTecnicoResponsavel deve ser usado dentro de TecnicoResponsavelProvider.');
  }
  return context;
};

export const TecnicoResponsavelProvider = ({ children }) => {
  const [tecnicos, setTecnicos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCad, setShowCad] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [tecnicoInicial, setTecnicoInicial] = useState(null);
  const selectResolverRef = useRef(null);
  const cadResolverRef = useRef(null);
  const { showAlert } = useAlert();
  const { showMessage } = useMessage();

  const executar = useCallback(async (acao, sucesso, erro) => {
    setLoading(true);
    try {
      const dados = await acao();
      if (sucesso) showAlert(sucesso);
      return dados;
    } catch (exception) {
      showMessage(`${erro}\n${exception?.message || exception}`, 'danger');
      return null;
    } finally {
      setLoading(false);
    }
  }, [showAlert, showMessage]);

  const listar = useCallback(async () => {
    const dados = await executar(
      service.listarTecnicosResponsaveis,
      null,
      'Erro ao buscar técnicos responsáveis!',
    );
    if (dados !== null) setTecnicos(dados || []);
    return dados;
  }, [executar]);

  const adicionar = async tecnico => {
    const salvo = await executar(
      () => service.criarTecnicoResponsavel(tecnico),
      'Técnico responsável adicionado com sucesso.',
      'Erro ao adicionar técnico responsável!',
    );
    if (salvo) setTecnicos(anteriores => [...anteriores, salvo]);
    return salvo;
  };

  const atualizar = async tecnico => {
    const salvo = await executar(
      () => service.atualizarTecnicoResponsavel(tecnico),
      'Técnico responsável alterado com sucesso.',
      'Erro ao alterar técnico responsável!',
    );
    if (salvo) {
      setTecnicos(anteriores => anteriores.map(item => item.id === salvo.id ? salvo : item));
    }
    return salvo;
  };

  const excluir = async id => {
    const resultado = await executar(
      () => service.excluirTecnicoResponsavel(id),
      'Técnico responsável excluído com sucesso.',
      'Erro ao excluir técnico responsável!',
    );
    if (resultado === null) return false;
    setTecnicos(anteriores => anteriores.filter(item => item.id !== id));
    return true;
  };

  const showTecnicoResponsavelCad = tecnico => new Promise(resolve => {
    setTecnicoInicial(tecnico || null);
    cadResolverRef.current = resolve;
    setShowCad(true);
  });

  const fecharCad = () => {
    setShowCad(false);
    setTecnicoInicial(null);
    cadResolverRef.current?.(null);
    cadResolverRef.current = null;
  };

  const tecnicoSalvo = tecnico => {
    setShowCad(false);
    setTecnicoInicial(null);
    cadResolverRef.current?.(tecnico);
    cadResolverRef.current = null;
  };

  const handleShowTecnicoResponsavelSearch = async () => {
    await listar();
    return new Promise(resolve => {
      selectResolverRef.current = resolve;
      setShowSearch(true);
    });
  };

  const fecharSearch = () => {
    setShowSearch(false);
    selectResolverRef.current?.(null);
    selectResolverRef.current = null;
  };

  const selecionarTecnico = tecnico => {
    setShowSearch(false);
    selectResolverRef.current?.(tecnico || null);
    selectResolverRef.current = null;
  };

  const value = useMemo(() => ({
    tecnicos,
    loading,
    listar,
    adicionar,
    atualizar,
    excluir,
    showTecnicoResponsavelCad,
    handleShowTecnicoResponsavelSearch,
  }), [tecnicos, loading, listar]);

  return (
    <TecnicoResponsavelContext.Provider value={value}>
      {children}
      <TecnicoResponsavelCad
        show={showCad}
        onHide={fecharCad}
        initialTecnico={tecnicoInicial}
        onSaveTecnico={tecnicoSalvo}
      />
      <TecnicoResponsavelSearch
        show={showSearch}
        onHide={fecharSearch}
        onSelectTecnico={selecionarTecnico}
      />
    </TecnicoResponsavelContext.Provider>
  );
};
